import { cx } from "@light-technology/components";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import { useState } from "react";
import type { PlaybookContentFaq } from "../../state/api.types";
import { LinkMark } from "./PlaybookBody";

const portableTextComponents: PortableTextComponents = {
  block: {
    h2: ({ children }) => (
      <h2 className="mt-2xl text-xl font-semibold text-primary">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-xl text-lg font-semibold text-primary">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-lg text-md font-semibold text-primary">{children}</h4>
    ),
    normal: ({ children }) => (
      <p className="mt-md text-md leading-6 text-tertiary">{children}</p>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mt-md list-disc pl-xl text-md text-tertiary">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="mt-md list-decimal pl-xl text-md text-tertiary">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code className="rounded bg-secondary px-xs font-mono text-xs">
        {children}
      </code>
    ),
    link: LinkMark,
  },
};

export const PlaybookGuideFAQs = ({
  faqs,
}: {
  faqs: Pick<PlaybookContentFaq, "question" | "answer">[];
}) => {
  const [openItems, setOpenItems] = useState(new Set([0]));

  const handleToggle = (index: number) => {
    if (openItems.has(index)) {
      openItems.delete(index);
    } else {
      openItems.add(index);
    }
    setOpenItems(new Set(openItems));
  };

  return (
    <div className="flex flex-col gap-4">
      {faqs.map((faq, index) => (
        <div
          key={faq.question}
          className="border-b border-secondary pb-2xl last:border-b-0"
        >
          <h3>
            <button
              onClick={() => {
                handleToggle(index);
              }}
              className="group flex w-full cursor-pointer items-start justify-between gap-6 py-1 text-left select-none"
            >
              <span className="text-md font-semibold text-primary">
                {faq.question}
              </span>
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center text-fg-quaternary group-hover:text-tertiary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line
                    className={cx(
                      "origin-center rotate-0 transition duration-150 ease-out",
                      openItems.has(index) && "-rotate-90",
                    )}
                    x1="12"
                    y1="8"
                    x2="12"
                    y2="16"
                  />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </span>
            </button>
          </h3>
          <div
            className={cx(
              "grid transition-all duration-200 ease-out",
              openItems.has(index) ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="pr-12 *:first:mt-0">
                <PortableText
                  value={faq.answer as PortableTextBlock[]}
                  components={portableTextComponents}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
